import React, { useEffect, useMemo, useState } from 'react'
import termoEPoliticaRepository from './TermoEPoliticaRepository'
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  useTheme
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CancelIcon from '@material-ui/icons/Cancel'
import { Snackbar } from 'elentari'
import StatusComponent from './StatusComponent'
import { Warning } from '@material-ui/icons'

const label = {
  ACEITO: 'Este motorista aceitou o termo.',
  ACEITO_PARCIAL:
    'Este motorista aceitou o termo parcialmente. Os dados dados comerciais do mesmo não poderão ser compartilhados.',
  ACEITO_MANUAL: 'Este termo foi aceito manualmente.',
  REJEITADO:
    'Este motorista não aceitou o termo. Os dados dados comerciais do mesmo não poderão ser compartilhados.',
  REVOGADO:
    'Este motorista revogou o termo. Os dados dados comerciais do mesmo não poderão ser compartilhados.',
  CANCELADO: 'Este termo foi cancelado.',
  AGUARDANDO_ACEITE: 'Este termo está aguardando aceite.',
  ENVIO_PENDENTE: 'Este termo está pendente de envio.'
}

const Icon = ({ status }) => {
  const theme = useTheme()
  switch (status) {
    case 'accepted':
      return (
        <CheckCircleIcon
          style={{
            color: theme.palette.success.dark,
            width: 20,
            height: 20
          }}
        />
      )
    case 'alert':
      return (
        <Warning
          style={{
            color: theme.palette.warning.dark,
            width: 20,
            height: 20
          }}
        />
      )

    default:
      return (
        <CancelIcon
          style={{
            color: theme.palette.error.dark,
            width: 20,
            height: 20
          }}
        />
      )
  }
}

const TermoEPoliticaCard = ({ cpf, embarcador = false }) => {
  const [message, setMessage] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [canalEnvio, setCanalEnvio] = useState('')
  const [hasError, setHasError] = useState(false)

  const validationParams = useMemo(() => {
    let status = 'error'
    if (data?.status) {
      if (['ACEITO', 'ACEITO_MANUAL'].includes(data.status.enum)) {
        status = 'accepted'
      }
      if (['ACEITO_PARCIAL'].includes(data.status.enum)) {
        status = 'alert'
      }
    }
    return {
      status,
      withoutData: data === null
    }
  }, [data])

  const cleanup = () => {
    setLoading(false)
    setData(null)
    setMessage('')
    setActionLoading(false)
    setCanalEnvio('')
    setHasError(false)
  }

  const handleChangeCanalEnvio = event => {
    setCanalEnvio(event.target.value)
  }

  const criarTermo = async () => {
    if (!canalEnvio) {
      setMessage('Selecione um canal de envio')
      return
    }
    setActionLoading(true)
    const response = await termoEPoliticaRepository.criar({ cpf, canalEnvio })

    if (response.ok && response.data?.data && response.data.ok) {
      setData(response.data.data)
      setMessage('Termo e política criado com sucesso')
    } else {
      setMessage('Erro ao enviar termo e política')
    }
    setActionLoading(false)
  }

  useEffect(() => {
    const loadTermoEPolitica = async () => {
      setLoading(true)
      const response = await termoEPoliticaRepository.verificar({ cpf })

      if (response.ok && response.data?.ok) {
        setData(response.data.data)
      }
      if (!response.ok && response.data.error.statusCode !== 404) {
        setHasError(true)
      }
      setLoading(false)
    }

    loadTermoEPolitica()

    return () => {
      cleanup()
    }
  }, [cpf])

  if (loading) {
    return <CircularProgress size={24} />
  }

  if (hasError) {
    return (
      <Grid container spacing={1} alignItems="center">
        <Grid item xs="auto">
          <Typography variant="h6">Termo e Política</Typography>
        </Grid>
        <Grid item xs="auto">
          <Box pt={0.5}>
            <Icon />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2">
            Houve um erro ao buscar o termo!
          </Typography>
        </Grid>
      </Grid>
    )
  }

  return (
    <>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs="auto">
          <Typography variant="h6">Termo e Política</Typography>
        </Grid>
        <Grid item xs="auto">
          <Box pt={0.5}>
            <Icon status={validationParams?.status} />
          </Box>
        </Grid>
        {!validationParams.withoutData && (
          <>
            <Grid item xs={12}>
              <Typography variant="body2">{label[data.status.enum]}</Typography>
            </Grid>
            <StatusComponent
              status={data.status.enum}
              termoId={data.termoConformidadeId}
              urlId={data.urlId}
              embarcador={embarcador}
            />
          </>
        )}
        {validationParams.withoutData && (
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2">
                  Não foi encontrado nenhum termo e política para o motorista.
                </Typography>
              </Grid>
              {!embarcador && (
                <>
                  <Grid item xs={12}>
                    <FormControl
                      component="fieldset"
                      onChange={handleChangeCanalEnvio}
                    >
                      <FormLabel component="legend">Canal de envio</FormLabel>
                      <RadioGroup
                        row
                        aria-label="canal-envio"
                        name="canalEnvio"
                        value={canalEnvio}
                      >
                        <FormControlLabel
                          value="SMS"
                          control={<Radio color="primary" />}
                          label="SMS"
                          labelPlacement="end"
                        />
                        <FormControlLabel
                          value="EMAIL"
                          control={<Radio color="primary" />}
                          label="E-mail"
                          labelPlacement="end"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      onClick={criarTermo}
                      variant="contained"
                      color="primary"
                      disabled={actionLoading}
                    >
                      {actionLoading && <CircularProgress size={24} />}
                      Solicitar termo
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
      <Snackbar message={message} onClose={() => setMessage('')} />
    </>
  )
}

export default TermoEPoliticaCard
